import React from "react";
import { blockTechnicalLabelClass } from "./layoutTokens";
import {
  BlockText,
  BlockQuote,
  BlockPAOCell,
  BlockMediaPlaceholder,
} from "./ContentBlocks";

/** Single row in the 12-column recipe board (visual teaching aid). */
export function RecipeRow({ label, math, children }) {
  return (
    <>
      <div className="grid grid-cols-12 border-t border-border dark:border-neutral-800 min-h-[88px]">
        {children}
      </div>
      {(label || math) && (
        <div className={`flex flex-wrap justify-between items-center gap-2 px-3 py-1.5 bg-surface-muted dark:bg-neutral-950 border-t border-border dark:border-neutral-800 ${blockTechnicalLabelClass}`}>
          {label && <span className="text-black dark:text-white font-medium">{label}</span>}
          {math && <span className="tracking-[0.04em] normal-case">{math}</span>}
        </div>
      )}
    </>
  );
}

export function RecipeCell({ span, children, className = "" }) {
  const col =
    span === 12
      ? "col-span-12"
      : span === 8
      ? "col-span-12 md:col-span-8"
      : span === 6
      ? "col-span-12 md:col-span-6"
      : span === 4
      ? "col-span-12 md:col-span-4"
      : "col-span-12";

  return (
    <div
      className={`${col} border-r border-border dark:border-neutral-800 last:border-r-0 flex flex-col justify-between p-3 md:p-4 ${className}`}
    >
      {children}
    </div>
  );
}

export function GridRecipeBoard() {
  return (
    <div className="mt-6 border border-border dark:border-neutral-800 bg-white dark:bg-black">
      <div className={`flex justify-between items-center px-3 py-2 border-b border-border dark:border-neutral-800 bg-surface-muted dark:bg-neutral-950 ${blockTechnicalLabelClass}`}>
        <span className="text-black dark:text-white font-medium">
          The 12-column grid · row shape examples
        </span>
        <span className="hidden sm:inline">Preview · dev</span>
      </div>
      <div className="grid grid-cols-12 px-2 py-2 border-b border-border dark:border-neutral-800 gap-0">
        {Array.from({ length: 12 }, (_, i) => (
          <div
            key={i}
            className="font-mono text-[9px] text-content-muted pl-1 border-l border-dashed border-border dark:border-neutral-700 first:border-l-0 first:pl-0"
          >
            {i + 1}
          </div>
        ))}
      </div>

      <RecipeRow label="Shape · 12" math="12 = 12">
        <RecipeCell span={12} className="!p-0">
          <BlockMediaPlaceholder label="Image · 12 × wide banner" spanLabel="s12" aspectClass="aspect-[3/1]" />
        </RecipeCell>
      </RecipeRow>

      <RecipeRow label="Shape · 8 + 4" math='8 + 4 = 12 · "lede + thumbnail"'>
        <RecipeCell span={8}>
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-content-muted mb-1">Text</span>
          <p className="text-[12px] leading-snug">
            A computational study mediating between sandstone, sightlines, and ground-level access.
          </p>
          <span className="font-mono text-[9px] self-end mt-2 px-1 py-px border border-border dark:border-neutral-600">
            s8
          </span>
        </RecipeCell>
        <RecipeCell span={4} className="!p-0">
          <BlockMediaPlaceholder label="Image · 1:1" variant="light" spanLabel="s4" aspectClass="aspect-square" />
        </RecipeCell>
      </RecipeRow>

      <RecipeRow label="Shape · 6 + 6" math='"quote + image"'>
        <RecipeCell span={6}>
          <BlockQuote
            quote="The first study to engage the sandstone as a living surface."
            attribution="Studio jury"
          />
          <span className="font-mono text-[9px] mt-2 text-content-muted">s6</span>
        </RecipeCell>
        <RecipeCell span={6} className="!p-0">
          <BlockMediaPlaceholder label="Image · 1:1" variant="dark" spanLabel="s6" aspectClass="aspect-square" />
        </RecipeCell>
      </RecipeRow>

      <RecipeRow label="Shape · 4 + 4 + 4" math='"P/A/O triplet"'>
        <RecipeCell span={4}>
          <BlockPAOCell role="P/A/O — Problem" body="Site circulation disconnected from the heritage rock face." />
          <span className="font-mono text-[9px] mt-auto pt-2 text-content-muted">s4</span>
        </RecipeCell>
        <RecipeCell span={4}>
          <BlockPAOCell role="P/A/O — Approach" body="Parametric mediation between program and sandstone edge." />
          <span className="font-mono text-[9px] mt-auto pt-2 text-content-muted">s4</span>
        </RecipeCell>
        <RecipeCell span={4}>
          <BlockPAOCell role="P/A/O — Outcome" body="Massing threading public access toward the harbour." />
          <span className="font-mono text-[9px] mt-auto pt-2 text-content-muted">s4</span>
        </RecipeCell>
      </RecipeRow>

      <RecipeRow label="Shape · 8 + 4" math='"live + caption"'>
        <RecipeCell span={8} className="!p-0">
          <BlockMediaPlaceholder label="Live · 8 × 4:3 · 3D / compare" variant="darker" spanLabel="s8" aspectClass="aspect-[4/3]" />
        </RecipeCell>
        <RecipeCell span={4}>
          <BlockText
            heading="Text"
            paragraphs={[
              "Drag the model to read massing against the rock interface — same row keeps narrative beside the widget.",
            ]}
          />
          <span className="font-mono text-[9px] text-content-muted mt-auto pt-2">s4</span>
        </RecipeCell>
      </RecipeRow>

      <RecipeRow label="Shape · 4 + 4 + 4" math='"steps · three across"'>
        <RecipeCell span={4}>
          <BlockText heading="Steps · 01" paragraphs={["Define program & site."]} />
          <span className="font-mono text-[9px] text-content-muted mt-auto pt-2">s4</span>
        </RecipeCell>
        <RecipeCell span={4}>
          <BlockText heading="Steps · 02" paragraphs={["Form-find against rules."]} />
          <span className="font-mono text-[9px] text-content-muted mt-auto pt-2">s4</span>
        </RecipeCell>
        <RecipeCell span={4}>
          <BlockText heading="Steps · 03" paragraphs={["Iterate, document, ship."]} />
          <span className="font-mono text-[9px] text-content-muted mt-auto pt-2">s4</span>
        </RecipeCell>
      </RecipeRow>
    </div>
  );
}
